import { State } from '@autopylot-internal/state';
import { CustomerInfo, INTRO_ELIGIBILITY_STATUS, LOG_LEVEL, PACKAGE_TYPE, Purchases, PurchasesOfferings, PurchasesPackage } from '@revenuecat/purchases-capacitor';
import { Device, DeviceInfo } from '@capacitor/device';
import { ENTITLEMENTS, IPaymentTerm } from './RevenueCatModel.ts';
import { IFreeTierLimits } from './IOfferMeta.ts';
import { EnvConfiguration } from '../../app/config/EnvConfiguration.ts';
import { AutoPylotPrincipal } from '../../auth/AutoPylotPrincipal.ts';
import { Logger } from '../../support/debug/Logger.ts';
import { PurchasesErrorAdapter } from './PurchasesErrorAdapter.ts';
import { IRevenueCatAdapter } from './RevenueCatSDKModel.ts';
import { StoreConfigs } from '../../app/config/StoreConfigs.ts';
import { eventually } from '../../support/helpers/eventually.ts';

export class RevenueCatNativeAdapter extends State<Record<string, never>> implements IRevenueCatAdapter {

    static inject = () => [
        EnvConfiguration,
        AutoPylotPrincipal,
        Logger,
        PurchasesErrorAdapter
    ];
    constructor(
        private config: EnvConfiguration,
        private principal: AutoPylotPrincipal,
        private logger: Logger,
        private errorAdapter: PurchasesErrorAdapter
    ){
        super({});
    }

    private isInitialized = false;

    private getApiKey = (): string => {
        if (this.deviceInfo.platform === 'android'){
            return this.config.REVENUE_CAT_ANDROID_PUBLIC_KEY;
        }
        if (this.deviceInfo.platform === 'ios'){
            return this.config.REVENUE_CAT_IOS_PUBLIC_KEY;
        }
        throw new Error(`${this.deviceInfo.platform} is not supported`);
    };

    init = async ({ withRetry }: { withRetry?: boolean } = {}) => {
        this.logger.info('RevenueCatNativeAdapter.init-ing');

        this.deviceInfo = await Device.getInfo();

        // change this to DEBUG when wanting to drive more logs to the OS logs
        // does not seem to increase logs we see from JS
        await Purchases.setLogLevel({ level: LOG_LEVEL.INFO });

        const apiKey = this.getApiKey();

        if (!apiKey){
            this.logger.info(`RevenueCatNativeAdapter cannot init, no key in config`);
            return;
        }

        try {
            const isGuest = !this.principal.email;

            const { isConfigured } = await Purchases.isConfigured();
            if (!isConfigured) {
                const appUserID = (!isGuest ? this.principal.userId : undefined);
                await Purchases.configure({ apiKey, appUserID, shouldShowInAppMessagesAutomatically: false });
                this.logger.info(`RevenueCatNativeAdapter: called configure() with userId ${appUserID}`);
            } else if (isGuest) {
                const { isAnonymous } = await Purchases.isAnonymous();
                if (!isAnonymous) {
                    await Purchases.logOut();
                    this.logger.info('RevenueCatNativeAdapter: called logOut()');
                } else {
                    this.logger.info('RevenueCatNativeAdapter: user is already anonymous');
                }
            } else {
                const { appUserID } = await Purchases.getAppUserID();
                if (appUserID !== this.principal.userId) {
                    await Purchases.logIn({ appUserID: this.principal.userId });
                    this.logger.info(`RevenueCatNativeAdapter: called logIn() with userId ${this.principal.userId}`);
                } else {
                    this.logger.info(`RevenueCatNativeAdapter: userId already set to ${this.principal.userId}`);
                }
            }

            if (!isGuest) {
                await Purchases.setEmail({ email: this.principal.email });
                this.logger.info(`RevenueCatNativeAdapter: set email to "${this.principal.email}"`);
            }

            if (withRetry) {
                await eventually(async () => this.fetchSubscriber(), 10000, 2000);
            } else {
                await this.fetchSubscriber();
            }

            if (withRetry) {
                await eventually(async () => this.fetchOffers(), 10000, 2000);
            } else {
                await this.fetchOffers();
            }

            if (!isGuest && !this.hasUpgradeOffer()){
                this.logger.error(`WARN: RevenueCatNativeAdapter: user ${this.principal.userId} cannot upgrade on ${this.deviceInfo.platform} b/c we did not get an offer from RevenueCat.`);
            }

            // this isn't critical to initialization, so ignore any errors
            try {
                await Purchases.enableAdServicesAttributionTokenCollection();
            } catch (e: any) {
                this.logger.error('Error: RevenueCatNativeAdapter failed invoking Purchases.enableAdServicesAttributionTokenCollection().', { innerError: e.toString() });
            }

            this.isInitialized = true;
            this.setState({ });

            this.logger.info('RevenueCatNativeAdapter.init-ed', this.getDiagnostics().diagnostics);
        }
        catch (err: any){
            if (!withRetry) {
                // retry without await so app start can proceed
                this.logger.info(`Retrying RevenueCatNativeAdapter initialize. Got ${err.toString()} ${JSON.stringify(err)}`);
                this.init({ withRetry: true });
            } else {
                this.logger.error(`Error: RevenueCatNativeAdapter tried to initialize and failed. Got ${err.toString()} ${JSON.stringify(err)}`);
            }
        }

    };

    // guaranteed to be set during init
    private deviceInfo!: DeviceInfo;

    // might not be set (eg if not android)
    private lastCustomerInfo?: CustomerInfo;
    private lastOffers?: PurchasesOfferings;
    private eligibleForTrial = false;

    private fetchSubscriber = async () => {
        const { customerInfo } = await Purchases.getCustomerInfo();
        this.lastCustomerInfo = customerInfo;
        this.setState({ });
    };

    private fetchOffers = async () => {
        this.lastOffers = await Purchases.getOfferings();
        this.eligibleForTrial = await this.checkTrialEligibility();
        this.setState({ });
    };

    private checkTrialEligibility = async () => {
        const availablePackages = this.lastOffers?.current?.availablePackages;
        if (!availablePackages) {
            return false;
        }
        if (this.deviceInfo.platform === 'ios'){
            // checkTrialOrIntroductoryPriceEligibility is only available on iOS
            try {
                const productIdentifiers = availablePackages.map(availablePackage => availablePackage.product.identifier);
                const introEligibilityStatuses = await Purchases.checkTrialOrIntroductoryPriceEligibility({ productIdentifiers });
                const ineligibleStatuses = [INTRO_ELIGIBILITY_STATUS.INTRO_ELIGIBILITY_STATUS_INELIGIBLE, INTRO_ELIGIBILITY_STATUS.INTRO_ELIGIBILITY_STATUS_UNKNOWN];
                return productIdentifiers.every(id => !ineligibleStatuses.includes(introEligibilityStatuses[id].status));
            } catch {
                // retry here seems like overkill, just default to false and move on...
                return false;
            }
        } else { // android
            // a user on Android is eligible if one of the subscription options has a freePhase entry
            for (const availablePackage of availablePackages) {
                for (const subscriptionOption of availablePackage.product.subscriptionOptions || []) {
                    if (subscriptionOption.freePhase && subscriptionOption.freePhase.price.amountMicros === 0) {
                        return true;
                    }
                }
            }
            return false;
        }
    };

    private purchasePackage = async (aPackage: PurchasesPackage) => {
        try {
            this.logger.info(`RevenueCatNativeAdapter.purchasePackage-ing`, aPackage);
            await Purchases.purchasePackage({ aPackage });
            this.logger.info(`RevenueCatNativeAdapter.purchasePackage-ed`, aPackage);
        }
        catch (err: any){
            try {
                this.logger.info(`RevenueCatNativeAdapter.purchasePackage-failed`, { err, jsonError: JSON.stringify(err)});
            }
            catch {
                // do nothing
            }

            throw this.errorAdapter.convert(err);
        }
    };

    canMakeRealPayment = () => {
        if (!this.isInitialized){
            return false;
        }
        if (!this.hasUpgradeOffer()){
            return false;
        }
        return true;
    };

    upgrade = async (term: IPaymentTerm) => {
        await this.purchasePackage(this.getUpgradePackage(term));
    };

    restore = async () => {
        try {
            this.logger.info(`RevenueCatNativeAdapter.restore-ing`);
            await Purchases.restorePurchases();
            this.logger.info(`RevenueCatNativeAdapter.restore-ed `);
        }
        catch (err: any){
            try {
                this.logger.info(`RevenueCatNativeAdapter.restore-failed`, { err, jsonError: JSON.stringify(err)});
            }
            catch {
                // do nothing
            }
            throw this.errorAdapter.convert(err);
        }
    };

    getFreeTierLimits = (): IFreeTierLimits | undefined => {
        const limits: any = this.lastOffers?.current?.metadata?.freeTierLimits;

        if (!limits){
            return undefined;
        }

        // I am being slightly defensive, b/c this is content managed by product
        // but I am not being overly defensive (eg is 0 expected, what about -1)
        // b/c if they have the right type, then they probably set it to what they want it to be
        // intentonally not adding sensible defaults b/c this shouldn't happen
        // that is handled upstream (and I won't want to duplicate)
        // and we'll be bubbling this to sentry, so it should be temporary
        for (const key of Object.keys(limits)) {
            if (typeof limits[key] !== 'number') {
                return undefined;
            }
        }

        return limits as any as IFreeTierLimits;
    };

    private hasUpgraded = () => {
        return this.getEntitlements().includes(ENTITLEMENTS.PLUS);
    };

    private hasUpgradeOffer = (term: IPaymentTerm = 'monthly') => {
        try {
            return Boolean(this.getUpgradePackage(term));
        } catch {
            return false;
        }
    };

    isEligibleForTrial = () => {
        return this.eligibleForTrial;
    };

    getManagementLink = () => {
        const entitlement = this.lastCustomerInfo?.entitlements.active[ENTITLEMENTS.PLUS];
        if (entitlement?.store === 'PLAY_STORE'){
            return `${StoreConfigs.android.subscribeURL}&sku=${entitlement.productIdentifier}`;
        }
        if (entitlement?.store === 'APP_STORE'){
            // https://developer.apple.com/documentation/storekit/in-app_purchase/original_api_for_in-app_purchase/subscriptions_and_offers/handling_subscriptions_billing
            // haven't yet seen a way to deeplink in, like we do in android
            // when running against App Store (sandbox)
            // this link won't help, and you actually need to go to Settings > App Store > Sandbox (manage) > Subscriptions
            return StoreConfigs.ios.subscribeURL;
        }
        return undefined;
    };

    private getUpgradePackage = (term: IPaymentTerm) => {
        const packages = this.lastOffers?.current?.availablePackages || [];

        const packageType = term === 'monthly' ? PACKAGE_TYPE.MONTHLY : PACKAGE_TYPE.ANNUAL;

        const packageForTerm = packages.find(p => p.packageType === packageType);
        if (!packageForTerm){
            throw new Error(`attempted to find package type ${packageType} for term ${term} but found none. Found "${packages.map(p => p.packageType).join(', ')}"`);
        }
        return packageForTerm;
    };

    private getEntitlements = (): string[] => Object.keys(this.lastCustomerInfo?.entitlements.active || {});

    getDiagnostics = () => {

        let diagnostics: { [label: string]: any } = {
            isInitialized: this.isInitialized,
        };

        if (this.isInitialized){
            diagnostics = {
                ...diagnostics,
                publicAPIKey: this.getApiKey(),
                hasUpgraded: this.hasUpgraded(),
                hasUpgradeOffer: this.hasUpgradeOffer(),
                isEligibleForTrial: this.isEligibleForTrial(),
                customerInfo: this.lastCustomerInfo,
                offers: this.lastOffers,
                canMakeRealPayment: this.canMakeRealPayment(),
                freeTierLimits: this.getFreeTierLimits() || '(none)',
            };
        }

        return {
            diagnostics,
            diagnosticsMethods: {
                fetchSubscriber: this.fetchSubscriber,
                fetchOffers: this.fetchOffers,
                purchasePackage: this.purchasePackage
            }
        };
    };
};
